import React, { useState, useEffect, useRef } from 'react';
import './PasswordInput.css';

// Lazy load zxcvbn to avoid bundling it immediately
let zxcvbn = null;
const loadZxcvbn = async () => {
  if (!zxcvbn) {
    const zxcvbnModule = await import('zxcvbn');
    zxcvbn = zxcvbnModule.default;
  }
  return zxcvbn;
};

const PasswordInput = ({
  value,
  onChange,
  onBlur,
  placeholder = "Enter your password",
  className = "",
  disabled = false,
  required = false,
  id = "password",
  name = "password",
  autoComplete = "current-password",
  showStrengthIndicator = false,
  minLength = 8,
  style = {},
  userInputs = [], // Additional inputs to check against (email, name, etc.)
  confirmValue = null, // Value to confirm against (for password confirmation fields)
  showConfirmationStatus = false, // Show confirmation matching status
  onStrengthChange = null, // Callback to notify parent of strength changes
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState(null);
  const [isZxcvbnLoaded, setIsZxcvbnLoaded] = useState(false);
  const strengthTimeoutRef = useRef(null);

  // Load zxcvbn on component mount if strength indicator is needed
  useEffect(() => {
    if (showStrengthIndicator) {
      loadZxcvbn().then(() => {
        setIsZxcvbnLoaded(true);
        // Evaluate current password if it exists
        if (value) {
          evaluatePasswordStrength(value);
        }
      });
    }
  }, [showStrengthIndicator]);

  // Evaluate password strength when value changes
  useEffect(() => {
    if (showStrengthIndicator && isZxcvbnLoaded && value) {
      // Debounce strength evaluation to avoid excessive calculations
      if (strengthTimeoutRef.current) {
        clearTimeout(strengthTimeoutRef.current);
      }
      strengthTimeoutRef.current = setTimeout(() => {
        evaluatePasswordStrength(value);
      }, 300);
    } else if (!value) {
      setStrength(null);
    }

    return () => {
      if (strengthTimeoutRef.current) {
        clearTimeout(strengthTimeoutRef.current);
      }
    };
  }, [value, showStrengthIndicator, isZxcvbnLoaded, userInputs]);

  const evaluatePasswordStrength = (password) => {
    if (!zxcvbn || !password) {
      setStrength(null);
      return;
    }

    try {
      const result = zxcvbn(password, userInputs);
      const strengthData = {
        score: result.score,
        feedback: result.feedback,
        crackTime: result.crack_times_display.offline_slow_hashing_1e4_per_second
      };
      setStrength(strengthData);
      
      // Notify parent of strength changes
      if (onStrengthChange) {
        onStrengthChange({
          score: result.score,
          isAcceptable: result.score >= 3,
          feedback: result.feedback
        });
      }
    } catch (error) {
      console.warn('Error evaluating password strength:', error);
      setStrength(null);
      if (onStrengthChange) {
        onStrengthChange({ score: null, isAcceptable: false, feedback: null });
      }
    }
  };

  const handleToggleVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  const getStrengthInfo = () => {
    if (!strength) return null;

    const { score, feedback, crackTime } = strength;
    
    const strengthLevels = [
      { label: 'Very Weak', color: '#dc2626', bgColor: '#fef2f2' },
      { label: 'Weak', color: '#ea580c', bgColor: '#fff7ed' },
      { label: 'Fair', color: '#d97706', bgColor: '#fffbeb' },
      { label: 'Good', color: '#16a34a', bgColor: '#f0fdf4' },
      { label: 'Strong', color: '#059669', bgColor: '#ecfdf5' }
    ];

    const level = strengthLevels[score] || strengthLevels[0];
    const isAcceptable = score >= 3; // zxcvbn score 3+ requirement

    return {
      ...level,
      score,
      isAcceptable,
      feedback,
      crackTime,
      message: isAcceptable 
        ? `✓ Strong password (${crackTime} to crack)` 
        : feedback.warning || 'Password too weak - please strengthen'
    };
  };

  const strengthInfo = getStrengthInfo();

  // Password confirmation matching logic
  const getConfirmationStatus = () => {
    if (!showConfirmationStatus || !confirmValue || !value) {
      return null;
    }

    const isMatching = value === confirmValue;
    const hasContent = value.length > 0 && confirmValue.length > 0;

    if (!hasContent) return null;

    return {
      isMatching,
      message: isMatching ? '✓ Passwords match' : '✗ Passwords do not match',
      color: isMatching ? '#16a34a' : '#dc2626',
      bgColor: isMatching ? '#f0fdf4' : '#fef2f2'
    };
  };

  const confirmationStatus = getConfirmationStatus();

  return (
    <div className="password-input-wrapper">
      <div className="password-input-container">
        <input
          type={showPassword ? 'text' : 'password'}
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={`password-input ${className} ${confirmationStatus && !confirmationStatus.isMatching ? 'confirmation-mismatch' : ''}`}
          disabled={disabled}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          style={style}
          {...props}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={handleToggleVisibility}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1} // Prevent tab focus on toggle button
        >
          {showPassword ? (
            // Eye with slash (hidden)
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          ) : (
            // Regular eye (visible)
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          )}
        </button>
      </div>

      {/* Password Strength Indicator */}
      {showStrengthIndicator && value && strengthInfo && (
        <div className="password-strength-indicator">
          <div className="strength-bar-container">
            <div 
              className={`strength-bar strength-score-${strengthInfo.score}`}
              style={{ 
                width: `${(strengthInfo.score + 1) * 20}%`,
                backgroundColor: strengthInfo.color
              }}
            />
          </div>
          <div 
            className={`strength-message ${strengthInfo.isAcceptable ? 'acceptable' : 'weak'}`}
            style={{ color: strengthInfo.color }}
          >
            <span className="strength-icon">
              {strengthInfo.isAcceptable ? '✓' : '⚠'}
            </span>
            {strengthInfo.message}
          </div>
          {strengthInfo.feedback.suggestions && strengthInfo.feedback.suggestions.length > 0 && (
            <div className="strength-suggestions">
              {strengthInfo.feedback.suggestions.map((suggestion, index) => (
                <div key={index} className="suggestion">
                  • {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Password Confirmation Status */}
      {confirmationStatus && (
        <div className="password-confirmation-status">
          <div 
            className={`confirmation-message ${confirmationStatus.isMatching ? 'matching' : 'mismatch'}`}
            style={{ 
              color: confirmationStatus.color,
              backgroundColor: confirmationStatus.bgColor
            }}
          >
            <span className="confirmation-icon">
              {confirmationStatus.isMatching ? '✓' : '✗'}
            </span>
            {confirmationStatus.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordInput; 